import { err, ok, type Result } from 'neverthrow';
import type { EdgeId, Graph } from '../../../types/graph';
import {
  CommandErrorKind,
  CommandKind,
  type CommandResult,
  type CommandError,
  type GraphCommand,
} from '../types';
import { bumpTopology } from '../utils';

export class SwapEdgeOrderCommand implements GraphCommand {
  readonly kind = CommandKind.SwapEdgeOrder;

  constructor(
    public readonly edgeIdA: EdgeId,
    public readonly edgeIdB: EdgeId,
  ) {}

  apply(graph: Readonly<Graph>): Result<CommandResult, CommandError> {
    const edgeA = graph.edges[this.edgeIdA];
    const edgeB = graph.edges[this.edgeIdB];

    if (!edgeA) {
      return err({ kind: CommandErrorKind.NotFound, message: `Edge ${this.edgeIdA} not found` });
    }
    if (!edgeB) {
      return err({ kind: CommandErrorKind.NotFound, message: `Edge ${this.edgeIdB} not found` });
    }

    if (edgeA.to.nodeId !== edgeB.to.nodeId || edgeA.to.portId !== edgeB.to.portId) {
      return err({ kind: CommandErrorKind.Validation, message: `Edges ${this.edgeIdA} and ${this.edgeIdB} are not on the same port` });
    }

    // Get all edges on the same port, sorted by current order
    const portEdges = Object.values(graph.edges)
      .filter((e) => e.to.nodeId === edgeA.to.nodeId && e.to.portId === edgeA.to.portId)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    // Swap positions of A and B in the sorted list
    const indexA = portEdges.findIndex((e) => e.id === edgeA.id);
    const indexB = portEdges.findIndex((e) => e.id === edgeB.id);
    [portEdges[indexA], portEdges[indexB]] = [portEdges[indexB], portEdges[indexA]];

    // Renumber all edges sequentially — no gaps, no duplicates
    const updatedEdges = { ...graph.edges };
    portEdges.forEach((e, i) => {
      updatedEdges[e.id] = { ...e, order: i };
    });
    const nextGraph: Graph = {
      ...graph,
      edges: updatedEdges,
    };

    return ok({
      graph: bumpTopology(nextGraph),
      inverse: new SwapEdgeOrderCommand(this.edgeIdA, this.edgeIdB),
      dirty: {
        nodes: [edgeA.to.nodeId],
        edges: portEdges.map((e) => e.id),
        topologyChanged: true,
      },
    });
  }
}
